import _styled, { default as _styled2, default as _styled3 } from "styled-components";
import React from "react";
import { padNumber } from "~/features/app/utils";
import { getThemeStyle, type Mode, type Theme } from "~/features/ui/get-theme-style";
import { Wrap } from "~/features/ui/wrap";
import { hasItems, takeFirst } from "~/features/app/utils";
import { type BenefitViewProps } from "~/features/blocks/inventory/benefits/benefit";
type SectionDataAttrs = Record<`data-${string}`, string | undefined>;
export type BenefitsBlockViewProps<TItem = Omit<BenefitViewProps, "count" | "mode" | "theme">> = {
  anchor?: Array<{
    id?: string | null;
    name?: string | null;
  }> | {
    id?: string | null;
    name?: string | null;
  } | null;
  getItemKey?: (item: TItem, index: number) => React.Key;
  hasTopDivider?: boolean | null;
  items?: TItem[] | null;
  mode?: string | null;
  renderItem: (item: TItem, index: number, count: string) => React.ReactNode;
  sectionProps?: React.ComponentPropsWithoutRef<"section"> & SectionDataAttrs;
  theme?: string | null;
};
export function BenefitsBlockView<TItem = Omit<BenefitViewProps, "count" | "mode" | "theme">>({
  anchor,
  getItemKey,
  hasTopDivider = true,
  items = [],
  mode = "light",
  renderItem,
  sectionProps,
  theme = "white"
}: BenefitsBlockViewProps<TItem>) {
  const themeStyle = getThemeStyle({
    mode: mode as Mode,
    theme: theme as Theme
  });
  // getThemeStyle maps the "white" theme to grey-20 (#efeff6); render it as true
  // white here (matching textMedia1_2) so a white-themed section is #ffffff, not lavender.
  // bg-primary is mode-aware (white in light, near-black in dark).
  const background = theme === "grey" ? themeStyle.background : {
    "backgroundColor": "var(--bg-primary)"
  };
  const benefits = items ?? [];
  if (!hasItems(benefits)) {
    return null;
  }
  return <_StyledSection {...sectionProps} data-theme={mode as Mode} id={takeFirst(anchor)?.id || undefined} data-anchor-name={takeFirst(anchor)?.name || undefined} $_css={[hasTopDivider && {
    "borderTopWidth": "1px"
  }, themeStyle.text, background, themeStyle.border]}>
      <Wrap mode={mode as Mode}>
        <_StyledDiv $_css2={[{
        "position": "relative",
        "zIndex": "10",
        "paddingLeft": "1.6rem",
        "paddingRight": "1.6rem",
        "@media (min-width: 1024px)": {
          "paddingLeft": "8rem",
          "paddingRight": "8rem"
        }
      }, background]}>
          <_StyledUl $_css3={[{
          "> :not([hidden]) ~ :not([hidden])": {
            "--tw-divide-y-reverse": "0",
            "borderTopWidth": "calc(1px * calc(1 - var(--tw-divide-y-reverse)))",
            "borderBottomWidth": "calc(1px * var(--tw-divide-y-reverse))"
          }
        }, themeStyle.border]}>
            {benefits.map((benefit, index) => <li key={getItemKey?.(benefit, index) || index}>{renderItem(benefit, index, padNumber(index + 1))}</li>)}
          </_StyledUl>
        </_StyledDiv>
      </Wrap>
    </_StyledSection>;
}
var _StyledSection = /*#__PURE__*/_styled("section").withConfig({
  componentId: "sc-sf-8wu5q0-0"
})`${p => p.$_css}`;
var _StyledDiv = _styled2("div").withConfig({
  componentId: "sc-sf-8wu5q0-1"
})`${p => p.$_css2}`;
var _StyledUl = /*#__PURE__*/_styled3("ul").withConfig({
  displayName: "benefits___StyledUl",
  componentId: "sc-1hq8knq-0"
})(["", ""], p => p.$_css3);